import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import blogArticles from '../data/blogArticles';

export default function BlogPost() {
  const { slug } = useParams();
  const article = blogArticles.find(a => a.slug === slug);

  if (!article) return <Navigate to="/blog" />;

  const url = `https://nomokratia.ai/blog/${article.slug}`;
  const imgUrl = article.image ? `https://nomokratia.ai${article.image}` : 'https://nomokratia.ai/images/blog/nomokratia-odigos.jpg';

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": imgUrl,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Organization",
      "name": "NOMOKRATIA.AI",
      "url": "https://nomokratia.ai"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PROTOS AI Agency IKE",
      "url": "https://ai-agency.gr",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nomokratia.ai/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "inLanguage": "el",
    "about": {
      "@type": "Thing",
      "name": article.category
    }
  };

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">{line.replace('### ', '')}</h3>;
      if (line.startsWith('---')) return <hr key={i} className="my-8 border-gray-200" />;
      if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
        const clean = line.replace(/\*/g, '').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
        return <p key={i} className="text-gray-500 italic mt-4">{clean}</p>;
      }
      if (line.trim() === '') return null;
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      if (parts.length > 1) {
        return <p key={i} className="text-gray-700 leading-relaxed mb-3">{parts.map((p, j) =>
          p.startsWith('**') ? <strong key={j} className="text-gray-900">{p.replace(/\*\*/g, '')}</strong> : p
        )}</p>;
      }
      return <p key={i} className="text-gray-700 leading-relaxed mb-3">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{article.title} | NOMOKRATIA.AI</title>
        <meta name="description" content={article.excerpt} />
        <meta name="author" content="NOMOKRATIA.AI" />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={imgUrl} />
        <meta property="og:site_name" content="NOMOKRATIA.AI" />
        <meta property="og:locale" content="el_GR" />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:author" content="NOMOKRATIA.AI" />
        <meta property="article:section" content={article.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={imgUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" alt="NOMOKRATIA Logo" className="h-12 w-auto" />
          </a>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/" className="text-gray-600 hover:text-slate-900 text-sm font-medium">{"\u0391\u03c1\u03c7\u03b9\u03ba\u03ae"}</a>
            <Link to="/blog" className="text-slate-900 text-sm font-bold border-b-2 border-slate-900 pb-1">Blog</Link>
          </nav>
        </div>
      </header>

      {article.image && (
        <div className="w-full h-64 md:h-96 overflow-hidden">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>
      )}

      <main className="container mx-auto max-w-3xl py-10 px-4">
        <Link to="/blog" className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-6 inline-block">
          &larr; {"\u038c\u03bb\u03b1 \u03c4\u03b1 \u03ac\u03c1\u03b8\u03c1\u03b1"}
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">{article.category}</span>
          <span className="text-gray-400 text-sm">{article.date}</span>
          <span className="text-gray-400 text-sm">{article.readTime}</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-8">{article.title}</h1>

        <article className="bg-white rounded-xl border border-gray-200 p-8 md:p-10">
          {renderContent(article.content)}
        </article>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8 text-center">
          <p className="text-blue-900 font-semibold mb-2">{"\u0394\u03bf\u03ba\u03b9\u03bc\u03ac\u03c3\u03c4\u03b5 \u03c4\u03bf NOMOKRATIA.AI \u03b4\u03c9\u03c1\u03b5\u03ac\u03bd"}</p>
          <p className="text-blue-700 text-sm mb-4">{"\u0391\u03bd\u03b1\u03b6\u03ae\u03c4\u03b7\u03c3\u03b7 \u03c3\u03b5 3.5+ \u03b5\u03ba\u03b1\u03c4\u03bf\u03bc\u03bc\u03cd\u03c1\u03b9\u03b1 \u03ad\u03b3\u03b3\u03c1\u03b1\u03c6\u03b1 \u2022 30 \u03b7\u03bc\u03ad\u03c1\u03b5\u03c2 \u03b4\u03c9\u03c1\u03b5\u03ac\u03bd"}</p>
          <a href="/" className="inline-block bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-3 rounded-lg text-sm transition-colors">{"\u039e\u03b5\u03ba\u03b9\u03bd\u03ae\u03c3\u03c4\u03b5 \u03c4\u03ce\u03c1\u03b1"}</a>
        </div>

        <div className="mt-10 text-center">
          <Link to="/blog" className="text-blue-600 hover:text-blue-800 font-semibold">
            &larr; {"\u03a0\u03af\u03c3\u03c9 \u03c3\u03c4\u03bf Blog"}
          </Link>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 px-4 text-center mt-8">
        <p className="text-gray-500 text-sm">&copy; 2026 NOMOKRATIA.AI by PROTOS AI Agency IKE</p>
      </footer>
    </div>
  );
}
